"use client";

import { useMemo, useState } from "react";

import type { FacelessWorkflowPreset } from "@/content/seo/types";
import {
  estimateVideoCostForConfiguration,
  type PublicPricingUnitCosts,
} from "@/libs/pricing";
import {
  estimateScriptDuration,
  formatEstimatedDuration,
  resolveFacelessVisualPlanningEstimate,
  resolveScriptDurationTarget,
} from "@/utils/scriptDuration";

import SeoCtaLink from "./SeoCtaLink";

type InputType = "idea" | "script" | "url";
type Platform = "youtube" | "tiktok" | "instagram" | "x";

const durationOptions = [30, 60, 180, 300, 600] as const;

const platformFormats: Record<Platform, string> = {
  youtube: "YouTube Shorts or standard video",
  tiktok: "TikTok vertical video",
  instagram: "Instagram Reel",
  x: "X video post",
};

const recommendedRatio = (
  platform: Platform,
  duration: number,
  selected: "9:16" | "16:9",
) => {
  if (platform === "tiktok" || platform === "instagram") return "9:16";
  if (platform === "youtube") return duration <= 60 ? "9:16" : "16:9";
  return selected;
};

export default function FacelessVideoLengthCalculator({
  unitCosts,
}: {
  unitCosts: PublicPricingUnitCosts;
}) {
  const [inputType, setInputType] = useState<InputType>("idea");
  const [platform, setPlatform] = useState<Platform>("youtube");
  const [targetDuration, setTargetDuration] = useState<(typeof durationOptions)[number]>(60);
  const [aspectRatio, setAspectRatio] = useState<"9:16" | "16:9">("9:16");
  const [script, setScript] = useState("");
  const [voiceRate, setVoiceRate] = useState(1);
  const [visualPace, setVisualPace] = useState(8);
  const usesScript = inputType === "script" && script.trim().length > 0;
  const scriptEstimate = useMemo(
    () => estimateScriptDuration(script, "English", voiceRate),
    [script, voiceRate],
  );
  const target = useMemo(
    () => resolveScriptDurationTarget(targetDuration, "English", voiceRate),
    [targetDuration, voiceRate],
  );
  const workflowPlan = useMemo(
    () =>
      resolveFacelessVisualPlanningEstimate({
        configuredDurationSeconds: targetDuration,
        isManualScript: false,
        language: "English",
        scriptText: "",
        visualPaceSeconds: visualPace,
        voiceRate,
      }),
    [targetDuration, visualPace, voiceRate],
  );
  const narrationDurationSeconds = usesScript
    ? scriptEstimate.seconds
    : targetDuration;
  const cost = useMemo(
    () =>
      estimateVideoCostForConfiguration({
        durationSeconds: workflowPlan.durationSeconds,
        imageCount: workflowPlan.estimatedImageCount,
        unitCosts,
      }),
    [unitCosts, workflowPlan.durationSeconds, workflowPlan.estimatedImageCount],
  );
  const platformRecommendedRatio = recommendedRatio(
    platform,
    targetDuration,
    aspectRatio,
  );
  const preset: FacelessWorkflowPreset = {
    aspectRatio,
    planDuration: targetDuration,
    ...(inputType === "url" ? { storyType: "url_to_video" as const } : {}),
  };
  const durationBucket =
    targetDuration < 60 ? "30s" : targetDuration === 60 ? "60s" : `${targetDuration / 60}m`;

  return (
    <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
      <div className="space-y-6">
        <fieldset>
          <legend className="text-sm font-semibold">Start from</legend>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {(["idea", "script", "url"] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={inputType === value}
                onClick={() => setInputType(value)}
                className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-semibold capitalize transition-colors ${
                  inputType === value
                    ? "border-violet-700 bg-violet-50 text-violet-800"
                    : "border-zinc-300 bg-white text-zinc-700 hover:border-violet-300"
                }`}
              >
                {value === "url" ? "URL" : value}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs leading-5 text-zinc-500">
            Choose a workflow type only. You do not need to paste a private idea or URL here.
          </p>
        </fieldset>

        <div className="grid gap-5 sm:grid-cols-2">
          <label className="text-sm font-semibold">
            Target platform
            <select
              value={platform}
              onChange={(event) => setPlatform(event.target.value as Platform)}
              className="mt-2 block min-h-11 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 focus:border-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option value="youtube">YouTube</option>
              <option value="tiktok">TikTok</option>
              <option value="instagram">Instagram</option>
              <option value="x">X</option>
            </select>
          </label>
          <label className="text-sm font-semibold">
            Target duration
            <select
              value={targetDuration}
              onChange={(event) => setTargetDuration(Number(event.target.value) as (typeof durationOptions)[number])}
              className="mt-2 block min-h-11 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 focus:border-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              {durationOptions.map((seconds) => (
                <option key={seconds} value={seconds}>{formatEstimatedDuration(seconds)}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold">
            Aspect ratio
            <select
              value={aspectRatio}
              onChange={(event) => setAspectRatio(event.target.value as "9:16" | "16:9")}
              className="mt-2 block min-h-11 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 focus:border-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option value="9:16">9:16 vertical</option>
              <option value="16:9">16:9 widescreen</option>
            </select>
          </label>
          <label className="text-sm font-semibold">
            Voice speed
            <select
              value={voiceRate}
              onChange={(event) => setVoiceRate(Number(event.target.value))}
              className="mt-2 block min-h-11 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 focus:border-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option value={0.85}>0.85x, measured</option>
              <option value={1}>1x, standard</option>
              <option value={1.15}>1.15x, brisk</option>
            </select>
          </label>
          <label className="text-sm font-semibold sm:col-span-2">
            Visual change pace
            <select
              value={visualPace}
              onChange={(event) => setVisualPace(Number(event.target.value))}
              className="mt-2 block min-h-11 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 focus:border-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              {[5, 6, 8, 10].map((seconds) => (
                <option key={seconds} value={seconds}>Every {seconds} seconds</option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <label htmlFor="length-script" className="text-sm font-semibold">
            Optional English script
          </label>
          <textarea
            id="length-script"
            value={script}
            onChange={(event) => setScript(event.target.value)}
            disabled={inputType !== "script"}
            placeholder={inputType === "script" ? "Paste narration to compare its estimated length with the workflow target. The text stays in your browser." : "Select Script above to estimate from your own narration."}
            className="mt-3 min-h-48 w-full rounded-xl border border-zinc-300 bg-white p-4 leading-7 text-zinc-900 placeholder:text-zinc-400 focus:border-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-200 disabled:bg-zinc-100 disabled:text-zinc-500"
          />
        </div>
      </div>

      <section
        aria-live="polite"
        className="rounded-xl bg-zinc-950 p-7 text-white lg:self-start"
      >
        <p className="text-sm font-semibold text-violet-300">Workflow plan</p>
        <h2 className="mt-2 text-2xl font-bold">Estimated production needs</h2>
        <dl className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <div>
            <dt className="text-sm text-zinc-400">Narration estimate</dt>
            <dd className="mt-1 text-3xl font-bold">
              {formatEstimatedDuration(narrationDurationSeconds)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-zinc-400">Word count</dt>
            <dd className="mt-1 text-xl font-semibold">
              {usesScript ? scriptEstimate.unitCount.toLocaleString() : `about ${target.targetUnits.toLocaleString()}`}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-zinc-400">Workflow target</dt>
            <dd className="mt-1 text-xl font-semibold">
              {formatEstimatedDuration(targetDuration)}
            </dd>
            {usesScript && narrationDurationSeconds !== targetDuration && (
              <p className="mt-1 text-xs leading-5 text-amber-200">
                Your script estimate differs from the selected workflow target.
              </p>
            )}
          </div>
          <div>
            <dt className="text-sm text-zinc-400">Visuals including cover</dt>
            <dd className="mt-1 text-xl font-semibold">{workflowPlan.estimatedImageCount.toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-sm text-zinc-400">Estimated credits</dt>
            <dd className="mt-1 text-xl font-semibold">
              {cost.minimumCredits.toLocaleString()}-{cost.maximumCredits.toLocaleString()}
            </dd>
          </div>
          <div className="sm:col-span-2 lg:col-span-1 xl:col-span-2">
            <dt className="text-sm text-zinc-400">Applied workflow format</dt>
            <dd className="mt-1 text-base font-semibold">
              {aspectRatio} · {platformFormats[platform]}
            </dd>
            {platformRecommendedRatio !== aspectRatio && (
              <p className="mt-2 text-xs leading-5 text-violet-200">
                Recommendation for this platform and duration: {platformRecommendedRatio}.
                Change the Aspect ratio field if you want to apply it.
              </p>
            )}
          </div>
        </dl>
        <SeoCtaLink
          locale="en"
          source="faceless-video-length-calculator"
          location="planner-results"
          landingPath="/tools/faceless-video-length-calculator"
          preset={preset}
          analyticsProps={{
            input_type: inputType,
            duration_bucket: durationBucket,
            aspect_ratio: aspectRatio,
          }}
          className="mt-8 inline-flex min-h-12 w-full items-center justify-center rounded-lg bg-violet-500 px-6 py-3 text-sm font-semibold text-white hover:bg-violet-400"
        >
          Use this plan in AriaFlow
        </SeoCtaLink>
        <p className="mt-5 text-xs leading-5 text-zinc-400">
          The planner runs in your browser. Script text is not added to the link,
          analytics, or AriaFlow attribution. Only the validated ratio, duration,
          and optional URL workflow preset continue to signup.
        </p>
        {unitCosts.isFallback && (
          <p className="mt-3 text-xs leading-5 text-amber-200">
            Live unit pricing was unavailable, so the credit range uses the marked fallback rates.
          </p>
        )}
      </section>
    </div>
  );
}
